import { useState } from 'react';
import { CheckCircle2, FileEdit, Shield } from 'lucide-react';
import PageLayout, { PageCTA } from '../components/PageLayout';
import { IMAGES } from '../mock';
import { useLang } from '../i18n/LanguageContext';
import RfqDialog from '../components/platform/RfqDialog';

export default function PartnersPage() {
  const { t } = useLang();
  const p = t('pages.partners');
  const steps = t('platform.partnersSteps');
  const rfq = t('platform.rfq');
  const [rfqOpen, setRfqOpen] = useState(false);
  return (
    <PageLayout crumb={p.breadcrumb} title={p.hero} subtitle={p.sub} heroImage={IMAGES.factory}>
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-navy text-center mb-12">{p.why}</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {p.whys.map((w, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-gray-50 border border-gray-100 hover:border-brand-yellow transition">
                <div className="shrink-0 w-10 h-10 rounded-full bg-brand-yellow/15 border border-brand-yellow/40 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-brand-yellow" />
                </div>
                <div className="text-brand-navy font-medium pt-1">{w}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <h2 className="text-3xl md:text-4xl font-bold text-brand-navy text-center mb-12">{p.process}</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 items-stretch">
            {steps.map((s, idx) => (
              <div key={s.n} className={`p-6 rounded-xl border flex flex-col ${idx === 0 ? 'bg-brand-navy text-white border-brand-navy shadow-xl' : 'bg-white border-gray-100 hover-lift hover:border-brand-yellow'}`}>
                <div className="text-brand-yellow font-black text-4xl">{s.n}</div>
                <div className={`font-bold text-lg mt-2 ${idx === 0 ? 'text-white' : 'text-brand-navy'}`}>{s.t}</div>
                <div className={`text-sm mt-2 ${idx === 0 ? 'text-white/80' : 'text-gray-600'}`}>{s.d}</div>
                {idx === 0 && (
                  <>
                    <p className="text-xs text-white/70 mt-4 leading-relaxed">{rfq.openHint}</p>
                    <button
                      type="button"
                      onClick={() => setRfqOpen(true)}
                      data-testid="rfq-open-button-partners"
                      className="mt-4 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-yellow text-brand-navy font-bold text-xs rounded-full uppercase tracking-wider hover:bg-yellow-400 transition self-start"
                    >
                      <FileEdit className="w-3.5 h-3.5" /> {rfq.cta}
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* Step 02 explanatory block */}
          {t('platform.shortlistIntro') && (
            <div className="mt-14 max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-brand-yellow/15 border border-brand-yellow text-brand-navy text-[11px] font-bold tracking-widest uppercase">02 · Short-list</div>
              <h3 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight mb-4">{t('platform.shortlistIntro').title}</h3>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p>{t('platform.shortlistIntro').p1}</p>
                <p>{t('platform.shortlistIntro').p2}</p>
                <p>{t('platform.shortlistIntro').p3}</p>
              </div>
              <div className="mt-6 flex items-center gap-3 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-brand-yellow" /> Accès sécurisé par lien personnel</span>
                <span className="text-gray-300">·</span>
                <span>Reçu par e-mail une fois votre cahier des charges validé par Yuville</span>
              </div>
            </div>
          )}

          {/* Step 03 explanatory block */}
          {t('platform.auditIntro') && (
            <div className="mt-14 max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-brand-yellow/15 border border-brand-yellow text-brand-navy text-[11px] font-bold tracking-widest uppercase">03 · Audit sur place</div>
              <h3 className="text-2xl md:text-3xl font-bold text-brand-navy leading-tight mb-4">{t('platform.auditIntro').title}</h3>
              <div className="space-y-3 text-gray-700 leading-relaxed">
                <p>{t('platform.auditIntro').p1}</p>
                <p>{t('platform.auditIntro').p2}</p>
              </div>
              <div className="mt-6 flex items-center gap-3 text-xs text-gray-500">
                <span className="inline-flex items-center gap-1"><Shield className="w-3.5 h-3.5 text-brand-yellow" /> Rapport documenté avec preuves</span>
                <span className="text-gray-300">·</span>
                <span>3 volets : Usine · Produit &amp; Qualité · Juridique &amp; Commercial</span>
              </div>
            </div>
          )}
        </div>
      </section>

      <PageCTA title={p.ctaTitle} body={p.ctaBody} />
      <RfqDialog open={rfqOpen} onOpenChange={setRfqOpen} />
    </PageLayout>
  );
}
